<script setup>
import { DateInput, Banner, Dropdown, ErrorHighlighter, Icon, InputLabel } from 'dmui_components'
import ReportFileInput from './ReportFileInput.vue'
import OrganizationDropdown from '../edit/OrganizationDropdown.vue'
import { computed } from 'vue'
import useReportTypeMgmt from '../composables/useReportTypeMgmt.js'
import ReportRepository from '../repository/ReportRepository.js'
import { useRoute } from 'vue-router'

const { getMappedDocumentType, getReportTypes } = useReportTypeMgmt()
const route = useRoute()

const props = defineProps({
	report: {
		type: Object,
		required: true
	},
	errors: {
		type: Object,
		default: () => ({})
	},
	file: {
		type: Object,
		required: true
	},
	source: {
		type: String,
		required: true
	}
})
const emit = defineEmits(['update:report', 'update:file', 'update:errors'])

const mappedDocumentType = computed(() => getMappedDocumentType(props.report?.document_type?.split(' ')[0]))

function onUpdateReport(field, value) {
	emit('update:report', { ...props.report, ...value })
	emit('update:errors', field)
}
function onUpdateFile(value) {
	emit('update:file', value)
	emit('update:errors', 'file')
}
const vueDatePickerProps = {
	yearRange: [new Date().getFullYear() - 10, new Date().getFullYear()]
}
</script>
<template>
	<div class="header-content">
		<h2 class="page-header">Add new report</h2>
		<slot />
	</div>
	<Banner v-if="errors?.report_name" type="error">
		Found existing '{{ mappedDocumentType }}' report. Ensure the core report type is unique for the selected organization and year combination.
	</Banner>
	<div class="input-section">
		<h3 class="primary-text bold">Report inputs</h3>
		<section class="bordered-content">
			<InputLabel for="report-type">Type</InputLabel>
			<ErrorHighlighter :errors="errors?.document_type">
				<Dropdown
					id="report-type"
					:data="report.document_type"
					size="small"
					:options="route.query?.documentType ? getReportTypes(report.document_type) : getReportTypes()"
					placeholder="Select type"
					@update:data="onUpdateReport('document_type', { document_type: $event })">
					<template #selected="{ data }">{{ data }} </template>
					<template #default="{ data }">{{ data }} </template>
				</Dropdown>
			</ErrorHighlighter>
			<InputLabel for="report-year">Year</InputLabel>
			<ErrorHighlighter :errors="errors?.document_year">
				<Dropdown
					id="report-year"
					:data="report.document_year"
					:options="Array.from({ length: 3 }, (_, i) => ReportRepository.getLatestYear() - i)"
					placeholder="Select year"
					placeholder-icon="uil-schedule"
					@update:data="onUpdateReport('document_year', { document_year: $event })">
					<template #leftIcon><Icon name="uil-schedule" size="midi" /></template>
					<template #selected="{ data }">{{ data }} </template>
					<template #default="{ data }">{{ data }} </template>
				</Dropdown>
			</ErrorHighlighter>
			<InputLabel for="company">Company name</InputLabel>
			<ErrorHighlighter :errors="errors?.org_id">
				<OrganizationDropdown id="company" :selected-org="report.org" @update:selected-org="onUpdateReport('org_id', { org: $event })" />
			</ErrorHighlighter>
		</section>
	</div>
	<div class="file-section">
		<span class="primary-text bold">Source document</span>
		<section class="bordered-content">
			<ReportFileInput
				:file-data="file"
				:url="report.document_url"
				:errors="errors"
				@update:file-data="onUpdateFile"
				@update:url="onUpdateReport('document_url', { document_url: $event })" />
		</section>
	</div>
	<div class="publication-date-section">
		<span class="primary-text bold"> Publication date </span>
		<section class="bordered-content">
			<DateInput
				id="publish-date"
				:data="report.published_at"
				:vue-date-picker-props="vueDatePickerProps"
				@update:data="onUpdateReport('published_at', { published_at: $event })" />
		</section>
	</div>
</template>
<style scoped lang="less">
:deep(.dropdown) {
	width: 100%;
	text-transform: capitalize;
}

.banner {
	max-width: none;
	margin: 0;
}
</style>
